import { createFileRoute, Outlet, useLocation } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { fetchInvoiceByIdQueryOptions } from "@/api/billing";
import { PageHeader } from "@/components/PageHeader";
import { useApiParameters } from "@/hooks/useApiParameters";
import { InvoiceDetailsNavigation } from "./-components/InvoiceDetailsNavigation";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/billing/$invoiceId",
)({
	loader: async ({ context, params }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);
		const { orgId, invoiceId } = params;

		// Preload invoice data
		await context.queryClient.ensureQueryData(
			fetchInvoiceByIdQueryOptions(userId, token, orgId, invoiceId),
		);

		return { userId, token, orgId, invoiceId };
	},
	component: InvoiceDetailsLayout,
});

function InvoiceDetailsLayout() {
	const { t } = useTranslation();
	const { userId, token } = Route.useLoaderData();
	const location = useLocation();

	// Determine active tab based on current route
	const getActiveTab = (): "overview" | "payments" | "refunds" | "disputes" => {
		const pathname = location.pathname;
		if (pathname.endsWith("/payments")) return "payments";
		if (pathname.endsWith("/refunds")) return "refunds";
		if (pathname.endsWith("/disputes")) return "disputes";
		return "overview"; // default to overview
	};

	return (
		<div className="flex flex-col h-full">
			<PageHeader
				title={t("billing.invoiceDetails.title")}
				description={t("billing.invoiceDetails.description")}
				showBackButton={true}
				backButtonText={t("common.back")}
				hideOnMobile={false}
			/>

			{/* Main content area with side navigation */}
			<div
				className="flex-1 flex h-[calc(100vh-4rem)] min-h-0 overflow-hidden"
				data-testid="invoice-details-container"
			>
				{/* Side Navigation */}
				<InvoiceDetailsNavigation
					activeTab={getActiveTab()}
					userId={userId}
					token={token}
				/>

				{/* Main Content Area */}
				<div
					className="flex-1 p-3 md:p-4 bg-base-200 min-h-0 overflow-auto"
					data-testid="invoice-details-main-content-area"
				>
					<Outlet />
				</div>
			</div>
		</div>
	);
}
